import React, { useState, useEffect } from "react";
import { Modal, Form, DatePicker, Radio, Row, Col, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { GetAppTwoTone, PageviewTwoTone } from "@material-ui/icons";

import CountrySelect from "../CountrySelect";
import SearchEngineSelect from "../SearchEngineSelect";
import { createReport } from "../../store/actions/reportActions";
import TagsSelect from "../Keywords/TagsSelect";
import LCButton from "../styles/LCButton";

const AddReport = () => {
  const dispatcher = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const currentDomain = useSelector((state) => state.domains.currentDomain);
  const keywordsObj = useSelector((state) => state.keywords);
  const isLoading = useSelector((state) => state.config.disableButton);
  const [manualReportForm] = Form.useForm();
  const { RangePicker } = DatePicker;
  const [startDate, setStartDate] = useState(moment().subtract(30, "days"));
  const [endDate, setEndDate] = useState(moment(new Date()));
  const [format, setFormat] = useState("csv");
  const dateFormat = "MMM Do YYYY";

  const disabledDate = (current) => {
    return current && current >= moment().endOf("day");
  };

  useEffect(() => {}, [keywordsObj]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const clearFormAndModal = () => {
    manualReportForm.resetFields();
    setIsModalVisible(false);
  };

  const onFinish = (values) => {
    let search = { q: {} };
    if (values.searchEngine) {
      search.q.searchEngine = {
        in: values.searchEngine,
      };
    }
    if (values.country) {
      search.q.country = {
        in: values.country,
      };
    }
    if (values.tags) {
      search.q.tags = {
        in: values.tags,
      };
    }
    let reportStartDate = startDate.format("YYYY-MM-DD");
    let reportEndDate = endDate.format("YYYY-MM-DD");
    if (values.inBetweenDates) {
      reportStartDate = values.inBetweenDates[0].format("YYYY-MM-DD");
      reportEndDate = values.inBetweenDates[1].format("YYYY-MM-DD");
    }
    dispatcher(
      createReport(
        currentDomain,
        search,
        reportStartDate,
        reportEndDate,
        format,
        values.type,
        clearFormAndModal,
      ),
    );
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <>
      <LCButton type='secondary' addMargin as='button' onClick={showModal}>
        <GetAppTwoTone style={{ fontSize: "20px" }} /> Generate Report
      </LCButton>
      <Modal
        title='Generate Report'
        visible={isModalVisible}
        onOk={() => manualReportForm.submit()}
        onCancel={() => setIsModalVisible(false)}
        okText='Generate'
        keyboard={false}
        maskClosable={false}
        width={1000}
        okButtonProps={{
          loading: isLoading,
        }}
        cancelButtonProps={{
          loading: isLoading,
        }}
        footer={[
          <LCButton
            type='primary'
            as='button'
            addMargin
            onClick={() => setIsModalVisible(false)}
            key='ModalVisibleFalse'>
            Cancel
          </LCButton>,
          <LCButton
            type='secondary'
            as='button'
            addMargin
            onClick={() => manualReportForm.submit()}
            key='ModalVisibleTrue'>
            Save
          </LCButton>,
        ]}>
        <Form
          form={manualReportForm}
          layout='vertical'
          name='addKeyworsForm'
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            format,
          }}>
          <Row gutter={16}>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item
                name='inBetweenDates'
                className='mb-1'
                label='Period'
                rules={[
                  {
                    required: true,
                    message: "Start and end dates required.",
                  },
                ]}>
                <RangePicker
                  defaultValue={[
                    moment(startDate, dateFormat),
                    moment(endDate, dateFormat),
                  ]}
                  disabledDate={disabledDate}
                  format={dateFormat}
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item
                name='type'
                className='mb-1'
                label='Type'
                rules={[
                  {
                    required: true,
                    message: "Please select type of the report.",
                  },
                ]}>
                <Select placeholder={<>Type</>} allowClear>
                  <Select.OptGroup label='Ranking'>
                    <Select.Option value='keyword'>Keywords</Select.Option>
                    <Select.Option value='pages'>Pages</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label='Competitors'>
                    <Select.Option value='competitors_keyword'>
                      Keywords
                    </Select.Option>
                    <Select.Option value='competitors_targets'>
                      Targets
                    </Select.Option>
                  </Select.OptGroup>
                </Select>
              </Form.Item>
            </Col>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item
                name='format'
                className='mb-1'
                label='Format'
                rules={[
                  {
                    required: true,
                    message: "Format is required.",
                  },
                ]}>
                <Radio.Group
                  onChange={(e) => setFormat(e.target.value)}
                  style={{ marginBottom: 8 }}
                  buttonStyle='solid'>
                  <Radio.Button value='csv'>Excel/CSV</Radio.Button>
                  <Radio.Button value='pdf'>PDF</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item
                name='searchEngine'
                className='mb-1'
                label='Search Engine'
                rules={[
                  { required: true, message: "Please select search engine." },
                ]}>
                <SearchEngineSelect />
              </Form.Item>
            </Col>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item
                name='country'
                className='mb-1'
                label='Country'
                rules={[{ required: true, message: "Please select country." }]}>
                <CountrySelect onlySearchEngine={true} />
              </Form.Item>
            </Col>
            <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24}>
              <Form.Item name='tags' className='mb-1' label='Tags'>
                <TagsSelect />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AddReport;
